import { formatPlanPrice } from '@/libs/utils/format-plan-price';
import { formatSuccessFeeRate } from '@/libs/utils/format-success-fee-rate';
import { Box, Flex, Stack, Text, chakra } from '@chakra-ui/react';
import { Check } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { planChoiceHighlights } from './plan-choice-features';
import type { PlanChoice } from './plan-choice-offer';

/**
 * One plan of the choice, picked like a radio button: its price, its rate on
 * affiliate sales and the few lines that make a brand pick it. Collab and
 * gifting fees sit under the cards, the full detail in the comparison.
 */
export const PlanChoiceCard = ({
  choice,
  checked,
  onSelect,
  current = false,
  currentSelectable = false,
  disabled = false,
}: {
  choice: PlanChoice;
  checked: boolean;
  onSelect: () => void;
  current?: boolean;
  currentSelectable?: boolean;
  disabled?: boolean;
}) => {
  const { t } = useTranslation();
  const locale = t('common.locale');
  const rate = formatSuccessFeeRate(choice.successFeeBasisPoints, locale);

  return (
    <chakra.button
      type="button"
      role="radio"
      aria-checked={checked}
      onClick={onSelect}
      disabled={(current && !currentSelectable) || disabled}
      display="flex"
      flexDirection="column"
      justifyContent="flex-start"
      alignItems="stretch"
      textAlign="left"
      px="18px"
      py="16px"
      borderRadius="16px"
      bg="white"
      borderWidth="1px"
      borderColor={checked ? 'primary.900' : 'cardBorder'}
      cursor="pointer"
      _disabled={{ cursor: 'default' }}
      transition="border-color .15s"
      _hover={{ borderColor: checked ? 'primary.900' : 'border.emphasized' }}
      _focusVisible={{ outline: '2px solid', outlineColor: 'primary.900', outlineOffset: '2px' }}
    >
      <Flex align="center" justify="space-between">
        <Text fontFamily="heading" fontSize="17px" fontWeight="600" letterSpacing="-0.01em">
          {t(`pricing.plan.${choice.id}.name`)}
        </Text>
        <Flex
          w="20px"
          h="20px"
          borderRadius="full"
          align="center"
          justify="center"
          bg={checked ? 'primary.900' : 'white'}
          borderWidth={checked ? 0 : '1.5px'}
          borderColor="border.emphasized"
          aria-hidden
        >
          {checked && <Box w="8px" h="8px" borderRadius="full" bg="white" />}
        </Flex>
      </Flex>
      {current && (
        <Text fontSize="12px" fontWeight="600" color="inkMuted" mt="3px">
          {t('pricing.cta.currentPlan')}
        </Text>
      )}
      <Text fontSize="12.5px" lineHeight="1.45" color="inkMuted" mt="3px" minH="36px">
        {t(`pricing.plan.${choice.id}.description`)}
      </Text>

      <Flex align="baseline" gap="5px" mt="12px">
        <Text fontFamily="heading" fontSize="30px" fontWeight="600" letterSpacing="-0.025em">
          {choice.price
            ? formatPlanPrice(choice.price.amount, choice.price.currencyCode, locale)
            : choice.id === 'free'
              ? 0
              : t('planChoice.paid.priceUnavailable')}
        </Text>
        <Text fontSize="13px" color="inkMuted">
          {t('pricing.plan.perMonth')}
        </Text>
      </Flex>
      <Text fontSize="12.5px" fontWeight="500" color="green.700" mt="2px">
        {choice.id === 'free'
          ? t('pricing.plan.affiliateFee', { rate })
          : t('pricing.plan.affiliateFeeOnTop', { rate })}
      </Text>
      <Box h="1px" bg="border.subtle" mt="12px" mb="12px" />

      <Stack gap="8px">
        {planChoiceHighlights(choice.id).map(({ key, values }) => (
          <Flex
            key={key}
            align="flex-start"
            gap="9px"
            fontSize="13px"
            lineHeight="1.4"
            color="navInactive"
          >
            <Check width={15} height={15} style={{ flex: 'none', marginTop: 1 }} aria-hidden />
            {t(key, values)}
          </Flex>
        ))}
      </Stack>
    </chakra.button>
  );
};
